import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Plus, X, Trash2, Sparkles, Printer, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { QUOTE_STATUSES } from "@/lib/config";
import { Card, Badge, Btn, EmptyState, inputCls, labelCls, AInput, ASelect, ATextarea, useSave } from "../AdminUI";
import AIQuoteModal from "./AIQuoteModal";

const statusColor = (s) => ({ Approved: "green", Declined: "red", Expired: "red", Sent: "blue", Viewed: "amber" }[s] || "gray");

function QuoteModal({ quote, customers, onClose, onSaved, canDelete }) {
  const [f, setF] = useState(quote || { customer_id: "", property: "", line_items: [{ description: "", qty: 1, rate: 0 }], discount: 0, tax: 0, notes: "", status: "Draft", expiration_date: "" });
  const [saving, run] = useSave();
  const [emailedAt, setEmailedAt] = useState(quote?.emailed_at || "");
  const [emailedTo, setEmailedTo] = useState(quote?.emailed_to || "");
  const [busy, setBusy] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const custEmail = customers.find((c) => c.id === f.customer_id)?.email || "";
  const custFirst = (customers.find((c) => c.id === f.customer_id)?.name || "there").split(" ")[0];
  const [em, setEm] = useState({ to: "", subject: "", message: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setItem = (i, k, v) => setF({ ...f, line_items: f.line_items.map((it, ix) => ix === i ? { ...it, [k]: v } : it) });
  const addItem = () => setF({ ...f, line_items: [...f.line_items, { description: "", qty: 1, rate: 0 }] });
  const rmItem = (i) => setF({ ...f, line_items: f.line_items.filter((_, ix) => ix !== i) });
  const subtotal = f.line_items.reduce((s, i) => s + (parseFloat(i.qty || 0) * parseFloat(i.rate || 0)), 0);
  const total = subtotal - parseFloat(f.discount || 0) + parseFloat(f.tax || 0);

  const persist = async () => {
    const body = { ...f, discount: parseFloat(f.discount || 0), tax: parseFloat(f.tax || 0),
      line_items: f.line_items.map((i) => ({ ...i, qty: parseFloat(i.qty || 0), rate: parseFloat(i.rate || 0) })) };
    if (quote) { await api.patch(`/admin/quotes/${quote.id}`, body); return quote.id; }
    const r = await api.post("/admin/quotes", body); return r.data.id;
  };
  const submit = (e) => { e.preventDefault(); run(async () => { await persist(); onSaved(); onClose(); }, "Quote saved"); };
  const del = () => { if (!window.confirm("Delete this quote?")) return; run(async () => { await api.delete(`/admin/quotes/${quote.id}`); onSaved(); onClose(); }, "Deleted"); };

  const printQuote = async () => {
    setBusy(true);
    try {
      const id = await persist(); onSaved();
      const r = await api.get(`/admin/quotes/${id}/print-html`);
      const w = window.open("", "_blank");
      if (!w) { toast.error("Allow pop-ups to print."); return; }
      w.document.write(r.data.html); w.document.close();
    } catch { toast.error("Unable to save the latest quote changes."); }
    finally { setBusy(false); }
  };
  const openEmail = async () => {
    setBusy(true);
    try {
      await persist(); onSaved();
      setEm({ to: custEmail, subject: "", message: `Hello ${custFirst},\n\nThank you for the opportunity to provide a quote for your property. Your quote is below.\n\nQuote: ${quote?.quote_id || ""}\nTotal: $${total.toFixed(2)}\n\nPlease review and contact us with any questions.\n\nThank you,\nVeterans Powerwash and Cleaning Services` });
      setEmailOpen(true);
    } catch { toast.error("Unable to save the latest quote changes."); }
    finally { setBusy(false); }
  };
  const sendEmail = async () => {
    if (!em.to || !em.to.includes("@")) { toast.error("Enter a valid recipient email."); return; }
    setBusy(true);
    try {
      const id = quote?.id || (await persist());
      const r = await api.post(`/admin/quotes/${id}/email`, { to: em.to, subject: em.subject, message: em.message });
      setEmailedAt(r.data.emailed_at); setEmailedTo(r.data.emailed_to);
      toast.success("Quote emailed successfully."); setEmailOpen(false); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Quote could not be emailed."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="quote-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <form onSubmit={submit} className="relative bg-white rounded-2xl w-full max-w-2xl p-6 space-y-4 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="font-head font-bold text-xl text-[var(--navy)] uppercase">{quote ? `Quote ${quote.quote_id}` : "New Quote"}</h2>
          <button type="button" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <ASelect label="Customer" value={f.customer_id} options={[{ value: "", label: "— Select —" }, ...customers.map((c) => ({ value: c.id, label: `${c.name} (${c.customer_id})` }))]} onChange={set("customer_id")} data-testid="quote-customer" />
          <AInput label="Property / Address" value={f.property} onChange={set("property")} />
          <ASelect label="Status" value={f.status} options={QUOTE_STATUSES} onChange={set("status")} data-testid="quote-status" />
          <AInput label="Expiration Date" type="date" value={f.expiration_date} onChange={set("expiration_date")} />
        </div>

        <div>
          <label className={labelCls}>Line Items</label>
          <div className="space-y-2">
            {f.line_items.map((it, i) => (
              <div key={i} className="flex gap-2 items-center">
                <input className={`${inputCls} flex-1`} placeholder="Description" value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} data-testid={`quote-item-desc-${i}`} />
                <input className={`${inputCls} w-16`} type="number" placeholder="Qty" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} />
                <input className={`${inputCls} w-24`} type="number" placeholder="Rate" value={it.rate} onChange={(e) => setItem(i, "rate", e.target.value)} />
                <span className="w-20 text-right text-sm text-gray-500">${(it.qty * it.rate || 0).toFixed(2)}</span>
                {f.line_items.length > 1 && <button type="button" onClick={() => rmItem(i)}><Trash2 className="w-4 h-4 text-gray-400" /></button>}
              </div>
            ))}
          </div>
          <Btn variant="ghost" type="button" onClick={addItem} className="mt-2"><Plus className="w-4 h-4" /> Add Item</Btn>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <AInput label="Discount ($)" type="number" value={f.discount} onChange={set("discount")} />
          <AInput label="Tax ($)" type="number" value={f.tax} onChange={set("tax")} />
        </div>
        <ATextarea label="Notes (visible to customer)" value={f.notes} onChange={set("notes")} />

        {emailedAt && <p className="text-xs text-gray-500" data-testid="quote-last-emailed">Last emailed: {new Date(emailedAt).toLocaleString()}{emailedTo ? ` to ${emailedTo}` : ""}</p>}

        <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-1">
          <div className="flex justify-between"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
          <div className="flex justify-between text-gray-500"><span>Discount</span><span>-${parseFloat(f.discount || 0).toFixed(2)}</span></div>
          <div className="flex justify-between text-gray-500"><span>Tax</span><span>+${parseFloat(f.tax || 0).toFixed(2)}</span></div>
          <div className="flex justify-between font-bold text-[var(--navy)] text-base pt-1 border-t border-gray-200 mt-1"><span>Total</span><span data-testid="quote-total">${total.toFixed(2)}</span></div>
        </div>

        <div className="flex flex-wrap gap-2 justify-between">
          {quote && canDelete ? <Btn variant="danger" type="button" onClick={del}><Trash2 className="w-4 h-4" /> Delete</Btn> : <span />}
          <div className="flex flex-wrap gap-2">
            <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
            <Btn variant="ghost" type="button" onClick={printQuote} loading={busy} data-testid="quote-print"><Printer className="w-4 h-4" /> Print Quote</Btn>
            <Btn variant="blue" type="button" onClick={openEmail} loading={busy} data-testid="quote-email"><Mail className="w-4 h-4" /> Email Quote</Btn>
            <Btn type="submit" loading={saving} data-testid="quote-save">Save Quote</Btn>
          </div>
        </div>
      </form>

      {emailOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4" data-testid="quote-email-modal">
          <div className="absolute inset-0 bg-black/50" onClick={() => setEmailOpen(false)} />
          <div className="relative bg-white rounded-2xl w-full max-w-lg p-6 space-y-3">
            <div className="flex items-center justify-between"><h3 className="font-head font-bold text-lg text-[var(--navy)] uppercase">Email Quote</h3><button onClick={() => setEmailOpen(false)}><X className="w-5 h-5" /></button></div>
            <AInput label="To" type="email" value={em.to} onChange={(e) => setEm({ ...em, to: e.target.value })} data-testid="email-to" />
            <AInput label="Subject (optional)" value={em.subject} onChange={(e) => setEm({ ...em, subject: e.target.value })} placeholder={`Your Veterans PWC Quote ${quote?.quote_id || ""}`} data-testid="email-subject" />
            <ATextarea label="Message" value={em.message} onChange={(e) => setEm({ ...em, message: e.target.value })} rows={7} data-testid="email-message" />
            <div className="flex justify-end gap-2"><Btn variant="ghost" onClick={() => setEmailOpen(false)}>Cancel</Btn><Btn variant="blue" onClick={sendEmail} loading={busy} data-testid="email-send"><Mail className="w-4 h-4" /> Send Quote</Btn></div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Quotes({ canDelete }) {
  const [quotes, setQuotes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [edit, setEdit] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const load = () => api.get("/admin/quotes").then((r) => setQuotes(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => {}); }, []);
  const custName = (id) => customers.find((c) => c.id === id)?.name || "—";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-head font-extrabold text-3xl text-[var(--navy)] uppercase tracking-tight">Quotes</h1>
        <div className="flex items-center gap-2">
          <Btn variant="ghost" onClick={() => setShowAI(true)} data-testid="ai-quote-btn" className="border border-[var(--action)] text-[var(--action)]"><Sparkles className="w-4 h-4" /> AI Quote</Btn>
          <Btn onClick={() => setShowNew(true)} data-testid="new-quote-btn"><Plus className="w-4 h-4" /> New Quote</Btn>
        </div>
      </div>
      <Card>
        {quotes.length === 0 ? <EmptyState icon={FileText} title="No quotes yet" hint="Create a quote for a customer." /> : (
          <div className="overflow-x-auto -m-1">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-gray-400 text-xs uppercase border-b border-gray-100">
                <th className="py-2 px-2">Quote</th><th className="px-2">Customer</th><th className="px-2">Total</th><th className="px-2">Status</th><th className="px-2">Created</th>
              </tr></thead>
              <tbody>
                {quotes.map((q) => (
                  <tr key={q.id} onClick={() => setEdit(q)} className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer" data-testid={`quote-row-${q.quote_id}`}>
                    <td className="py-3 px-2 font-semibold text-[var(--navy)]">{q.quote_id}</td>
                    <td className="px-2 text-gray-600">{custName(q.customer_id)}</td>
                    <td className="px-2 font-semibold">${q.total?.toFixed(2)}</td>
                    <td className="px-2"><Badge color={statusColor(q.status)}>{q.status}</Badge></td>
                    <td className="px-2 text-xs text-gray-400">{new Date(q.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {showNew && <QuoteModal customers={customers} onClose={() => setShowNew(false)} onSaved={load} canDelete={canDelete} />}
      {edit && <QuoteModal quote={edit} customers={customers} onClose={() => setEdit(null)} onSaved={load} canDelete={canDelete} />}
      {showAI && <AIQuoteModal customers={customers} onClose={() => setShowAI(false)} onCreated={() => load()} />}
    </div>
  );
}
